import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/Navbar";
import SeoHead from "../components/seo/SeoHead";
import Footer from "../components/Footer";
import API from "../services/api";

const THEME_KEY = "hookora_theme";

function ContactSupportPage() {
  const getStoredTheme = () => {
    if (typeof window === "undefined") return "light";
    return localStorage.getItem(THEME_KEY) || "light";
  };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: "suggestion",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [theme, setTheme] = useState(getStoredTheme());
  const [isMobile, setIsMobile] = useState(
    typeof window !== "undefined" ? window.innerWidth < 768 : false
  );

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const syncTheme = () => {
      setTheme(localStorage.getItem(THEME_KEY) || "light");
    };

    window.addEventListener("storage", syncTheme);
    window.addEventListener("hookora-preferences-updated", syncTheme);

    return () => {
      window.removeEventListener("storage", syncTheme);
      window.removeEventListener("hookora-preferences-updated", syncTheme);
    };
  }, []);

  const isDark = theme === "dark";

  const colors = useMemo(
    () => ({
      pageBg: isDark ? "#020617" : "#f8fafc",
      pageGradient: isDark
        ? "radial-gradient(circle at top left, rgba(59,130,246,0.12), transparent 28%), radial-gradient(circle at top right, rgba(124,58,237,0.10), transparent 24%)"
        : "none",
      cardBg: isDark ? "rgba(15,23,42,0.78)" : "#ffffff",
      cardBgStrong: isDark ? "rgba(15,23,42,0.92)" : "#ffffff",
      softSurface: isDark ? "rgba(30,41,59,0.68)" : "#f9fafb",
      badgeBg: isDark ? "rgba(67,56,202,0.18)" : "#eef2ff",
      badgeText: isDark ? "#c7d2fe" : "#4338ca",
      border: isDark ? "rgba(148,163,184,0.18)" : "#e5e7eb",
      borderStrong: isDark ? "rgba(148,163,184,0.28)" : "#d1d5db",
      text: isDark ? "#f8fafc" : "#111827",
      textMuted: isDark ? "#94a3b8" : "#6b7280",
      textSoft: isDark ? "#cbd5e1" : "#4b5563",
      inputBg: isDark ? "#0f172a" : "#ffffff",
      inputText: isDark ? "#f8fafc" : "#111827",
      shadow: isDark
        ? "0 14px 40px rgba(2, 6, 23, 0.45)"
        : "0 10px 30px rgba(15, 23, 42, 0.05)",
      successBg: isDark ? "rgba(20,83,45,0.30)" : "#f0fdf4",
      successBorder: isDark ? "rgba(74,222,128,0.30)" : "#bbf7d0",
      successText: isDark ? "#bbf7d0" : "#166534",
      errorBg: isDark ? "rgba(127,29,29,0.25)" : "#fef2f2",
      errorBorder: isDark ? "rgba(248,113,113,0.35)" : "#fecaca",
      errorText: isDark ? "#fecaca" : "#b91c1c"
    }),
    [isDark]
  );

  const inputStyle = {
    width: "100%",
    padding: "14px",
    borderRadius: "12px",
    border: `1px solid ${colors.borderStrong}`,
    boxSizing: "border-box",
    fontSize: "15px",
    background: colors.inputBg,
    color: colors.inputText
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);
      setError("");
      setSuccessMessage("");

      const response = await API.post("/users/contact-support", {
        name: formData.name.trim(),
        email: formData.email.trim(),
        category: formData.category,
        message: formData.message.trim()
      });

      setSubmitted(true);
      setSuccessMessage(response.data.message);
      setFormData({
        name: "",
        email: "",
        category: "suggestion",
        message: ""
      });
    } catch (err) {
      setError(
        err?.response?.data?.detail || "Failed to send message. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{ background: colors.pageBg, backgroundImage: colors.pageGradient, minHeight: "100vh" }}>
      <SeoHead
        title="Contact & Support | Hookora"
        description="Contact Hookora for support, suggestions, reviews, complaints, and billing-related questions."
        path="/contact-support"
      />

      <Navbar />

      <div
        style={{
          maxWidth: "920px",
          margin: "0 auto",
          padding: isMobile ? "24px 14px 48px" : "36px 18px 60px"
        }}
      >
        <div
          style={{
            background: colors.cardBg,
            border: `1px solid ${colors.border}`,
            borderRadius: "24px",
            padding: isMobile ? "22px" : "30px",
            boxShadow: colors.shadow,
            backdropFilter: isDark ? "blur(14px)" : "none"
          }}
        >
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
              padding: "8px 12px",
              borderRadius: "999px",
              background: colors.badgeBg,
              color: colors.badgeText,
              fontSize: "12px",
              fontWeight: "700",
              marginBottom: "14px",
              border: isDark ? `1px solid ${colors.border}` : "none"
            }}
          >
            Customer Support
          </div>

          <h1
            style={{
              marginTop: 0,
              marginBottom: "10px",
              color: colors.text,
              fontSize: isMobile ? "30px" : "36px",
              lineHeight: "1.1"
            }}
          >
            Contact & Support
          </h1>

          <p
            style={{
              color: colors.textMuted,
              lineHeight: "1.7",
              marginBottom: "28px",
              fontSize: "15px"
            }}
          >
            Have a suggestion, review, complaint, billing question, or general support request?
            We’d love to hear from you.
          </p>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1.05fr 0.95fr",
              gap: "22px"
            }}
          >
            <div
              style={{
                background: colors.softSurface,
                border: `1px solid ${colors.border}`,
                borderRadius: "18px",
                padding: "20px",
                backdropFilter: isDark ? "blur(10px)" : "none"
              }}
            >
              <h2 style={{ marginTop: 0, color: colors.text, fontSize: "22px" }}>
                Reach out to Hookora
              </h2>

              <p style={{ color: colors.textSoft, lineHeight: "1.8" }}>
                Use this page to contact Hookora for:
              </p>

              <ul style={{ color: colors.textSoft, lineHeight: "1.9", paddingLeft: "20px" }}>
                <li>suggestions and product ideas</li>
                <li>reviews and feedback</li>
                <li>complaints and service issues</li>
                <li>billing or subscription questions</li>
                <li>general customer support</li>
              </ul>

              <div
                style={{
                  marginTop: "20px",
                  padding: "16px",
                  background: colors.cardBgStrong,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "14px"
                }}
              >
                <div style={{ color: colors.text, fontWeight: "700", marginBottom: "8px" }}>
                  Support email
                </div>
                <div style={{ color: colors.textSoft, lineHeight: "1.7" }}>
                  support@hookora.app
                </div>
              </div>

              <div
                style={{
                  marginTop: "14px",
                  padding: "16px",
                  background: colors.cardBgStrong,
                  border: `1px solid ${colors.border}`,
                  borderRadius: "14px"
                }}
              >
                <div style={{ color: colors.text, fontWeight: "700", marginBottom: "8px" }}>
                  Response time
                </div>
                <div style={{ color: colors.textSoft, lineHeight: "1.7" }}>
                  We aim to respond to support requests as quickly as possible.
                </div>
              </div>
            </div>

            <div
              style={{
                background: colors.cardBgStrong,
                border: `1px solid ${colors.border}`,
                borderRadius: "18px",
                padding: "20px",
                backdropFilter: isDark ? "blur(10px)" : "none"
              }}
            >
              <h2 style={{ marginTop: 0, color: colors.text, fontSize: "22px" }}>
                Send a message
              </h2>

              {!submitted ? (
                <form onSubmit={handleSubmit}>
                  <div style={{ marginBottom: "12px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: colors.textSoft,
                        fontWeight: "600",
                        fontSize: "14px"
                      }}
                    >
                      Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Your name"
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ marginBottom: "12px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: colors.textSoft,
                        fontWeight: "600",
                        fontSize: "14px"
                      }}
                    >
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="Your email"
                      style={inputStyle}
                    />
                  </div>

                  <div style={{ marginBottom: "12px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: colors.textSoft,
                        fontWeight: "600",
                        fontSize: "14px"
                      }}
                    >
                      Category
                    </label>
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleChange}
                      style={inputStyle}
                    >
                      <option value="suggestion">Suggestion</option>
                      <option value="review">Review</option>
                      <option value="complaint">Complaint</option>
                      <option value="billing">Billing</option>
                      <option value="support">General Support</option>
                    </select>
                  </div>

                  <div style={{ marginBottom: "14px" }}>
                    <label
                      style={{
                        display: "block",
                        marginBottom: "8px",
                        color: colors.textSoft,
                        fontWeight: "600",
                        fontSize: "14px"
                      }}
                    >
                      Message
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help"
                      rows={6}
                      style={{
                        ...inputStyle,
                        resize: "vertical"
                      }}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    style={{
                      width: "100%",
                      padding: "14px",
                      background: loading ? "#9ca3af" : "linear-gradient(135deg, #2563eb, #7c3aed)",
                      color: "white",
                      border: "none",
                      borderRadius: "12px",
                      cursor: loading ? "not-allowed" : "pointer",
                      fontWeight: "700",
                      fontSize: "15px"
                    }}
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </button>

                  {error && (
                    <div
                      style={{
                        marginTop: "16px",
                        color: colors.errorText,
                        background: colors.errorBg,
                        border: `1px solid ${colors.errorBorder}`,
                        borderRadius: "12px",
                        padding: "12px",
                        lineHeight: "1.5"
                      }}
                    >
                      {error}
                    </div>
                  )}
                </form>
              ) : (
                <div
                  style={{
                    color: colors.successText,
                    background: colors.successBg,
                    border: `1px solid ${colors.successBorder}`,
                    borderRadius: "14px",
                    padding: "16px",
                    lineHeight: "1.7"
                  }}
                >
                  {successMessage || "Your message has been sent successfully."}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default ContactSupportPage;